import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { VoteAggregate, VoteChoice, Tile, NakiAction } from '../../types/mjai';
import { getTileLabel } from '../../utils/tileHelpers';
import TileImage from '../tiles/TileImage';
import TileSelector from '../tiles/TileSelector';
import Button from '../common/Button';
import { CertifiedBadge } from '../auth/LoginButton';
import { useAuth } from '../../contexts/AuthContext';
import CommentThread from './CommentThread';
import { useCreateComment } from '../../hooks/useComments';
import type { VotePayload } from '../../api/votes';

const TOP_N = 4;

const NAKI_LABELS: Record<NakiAction, string> = {
  chi: 'チー',
  pon: 'ポン',
  kan: 'カン',
  none: '鳴かない',
};

function getChoiceLabel(choice: Pick<VoteChoice, 'tile' | 'riichi' | 'naki_action'>): string {
  if (choice.naki_action) return NAKI_LABELS[choice.naki_action];
  if (choice.tile) {
    const base = getTileLabel(choice.tile);
    return choice.riichi ? `${base} リーチ` : base;
  }
  return '?';
}

function getMyVoteLabel(myVote: VoteAggregate['my_vote']): string {
  if (!myVote) return '';
  return getChoiceLabel({ tile: myVote.chosen_tile, riichi: myVote.riichi, naki_action: myVote.naki_action });
}

function choiceKey(c: Pick<VoteChoice, 'tile' | 'riichi' | 'naki_action'>): string {
  return `${c.tile ?? ''}|${c.riichi ?? ''}|${c.naki_action ?? ''}`;
}

interface VotePanelProps {
  handId: string;
  title?: string | null;
  situationType?: 'menzen' | 'fuuro' | 'naki' | null;
  aggregate: VoteAggregate | null;
  isLoading: boolean;
  onVote: (payload: VotePayload) => Promise<void>;
}

export default function VotePanel({
  handId,
  title,
  situationType,
  aggregate,
  isLoading,
  onVote,
}: VotePanelProps) {
  const { user, login } = useAuth();
  const createComment = useCreateComment(handId);
  const [replyingToVoteId, setReplyingToVoteId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isReplySubmitting, setIsReplySubmitting] = useState(false);

  // 副露後・門前
  const [selectedTile, setSelectedTile] = useState<Tile | null>(null);
  const [riichi, setRiichi] = useState(false);
  // 鳴き判断
  const [nakiAction, setNakiAction] = useState<NakiAction | null>(null);
  // 共通
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllResults, setShowAllResults] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const handleReplySubmit = async () => {
    if (!replyText.trim()) return;
    setIsReplySubmitting(true);
    try {
      await createComment.mutateAsync({ content: replyText.trim() });
      setReplyingToVoteId(null);
      setReplyText('');
    } finally {
      setIsReplySubmitting(false);
    }
  };

  const myVote = aggregate?.my_vote;
  const totalVotes = aggregate?.total_votes ?? 0;

  const canSubmit = situationType === 'naki' ? !!nakiAction : !!selectedTile;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const payload: VotePayload = { comment: comment || undefined };
      if (situationType === 'naki') {
        payload.naki_action = nakiAction!;
      } else {
        payload.chosen_tile = selectedTile!;
        if (situationType === 'menzen') payload.riichi = riichi;
      }
      await onVote(payload);
      setSelectedTile(null);
      setRiichi(false);
      setNakiAction(null);
      setComment('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const choices = aggregate?.choices ?? [];
  const visibleChoices = showAllResults ? choices : choices.slice(0, TOP_N);
  const hiddenCount = choices.length - TOP_N;

  // Determine voter list per choice
  const votersForChoice = (c: VoteChoice) => {
    const key = choiceKey(c);
    return (aggregate?.votes ?? [])
      .filter((v) => choiceKey({ tile: v.chosen_tile, riichi: v.riichi, naki_action: v.naki_action }) === key)
      .sort((a, b) => (b.user?.is_pro ? 1 : 0) - (a.user?.is_pro ? 1 : 0));
  };

  return (
    <div className="border rounded-xl bg-white overflow-hidden">
      <div className="p-3 bg-green-50 border-b">
        <h2 className="font-semibold text-green-900">
          {situationType === 'naki' ? '鳴く？鳴かない？' : 'あなたなら何を切る？'}
          <span className="ml-2 text-sm font-normal text-green-600">{totalVotes}票</span>
        </h2>
      </div>

      <div className="p-4 space-y-4">
        {/* Vote results */}
        {aggregate && choices.length > 0 && (
          <div className="space-y-2">
            {visibleChoices.map((c) => {
              const key = choiceKey(c);
              const pct = totalVotes > 0 ? Math.round((c.count / totalVotes) * 100) : 0;
              const myKey = myVote ? choiceKey({ tile: myVote.chosen_tile, riichi: myVote.riichi, naki_action: myVote.naki_action }) : null;
              const isMyChoice = myKey === key;
              const hasProVotes = c.pro_count > 0;
              const isExpanded = expandedKey === key;
              const voters = votersForChoice(c);

              return (
                <div key={key}>
                  <div
                    onClick={() => setExpandedKey(isExpanded ? null : key)}
                    className={`flex items-center gap-3 p-2 rounded-lg transition-colors cursor-pointer
                      ${isMyChoice ? 'bg-green-50 border border-green-200' : 'bg-gray-50 hover:bg-gray-100'}
                      ${hasProVotes ? 'ring-2 ring-yellow-400' : ''}
                    `}
                  >
                    {/* Icon / tile */}
                    {c.naki_action ? (
                      <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 shrink-0">
                        {NAKI_LABELS[c.naki_action][0]}
                      </div>
                    ) : c.tile ? (
                      <TileImage tile={c.tile} size="sm" />
                    ) : null}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs mb-1">
                        <span className="font-medium">{getChoiceLabel(c)}</span>
                        {isMyChoice && <span className="text-green-600 text-xs">✓ あなた</span>}
                        {hasProVotes && (
                          <span className="inline-flex items-center gap-0.5 bg-yellow-100 text-yellow-800 text-xs px-1.5 py-0.5 rounded-full font-medium">
                            <span className="text-yellow-500">★</span> PRO×{c.pro_count}
                          </span>
                        )}
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-400 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-bold text-gray-700">{pct}%</div>
                      <div className="text-xs text-gray-400">{c.count}票</div>
                    </div>
                    <span className="text-gray-400 text-xs shrink-0">{isExpanded ? '▲' : '▼'}</span>
                  </div>

                  {/* Voter list */}
                  {isExpanded && (
                    <div className="mt-1 mb-2 ml-2 pl-3 border-l-2 border-green-200 space-y-2">
                      {voters.map((v) => {
                        const voteUsername = v.user ? v.user.x_username : null;
                        const isReplyingToThis = replyingToVoteId === v.id;
                        return (
                          <div key={v.id} className="py-0.5 space-y-1">
                            <div className="flex items-start gap-2">
                              {v.user ? (
                                <Link
                                  to={`/users/${v.user.x_username}`}
                                  className="flex items-center gap-1.5 hover:opacity-75 shrink-0"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {v.user.x_avatar_url ? (
                                    <img src={v.user.x_avatar_url} alt="" className="w-5 h-5 rounded-full shrink-0" />
                                  ) : (
                                    <div className="w-5 h-5 rounded-full bg-gray-200 shrink-0 flex items-center justify-center text-xs text-gray-500">
                                      {v.user.x_display_name[0]}
                                    </div>
                                  )}
                                  <span className="text-xs font-medium text-gray-700">{v.user.x_display_name}</span>
                                  {v.user.is_pro && <CertifiedBadge />}
                                </Link>
                              ) : (
                                <span className="text-xs text-gray-400 shrink-0">匿名</span>
                              )}
                              {v.comment && (
                                <span className="text-xs text-gray-500 leading-tight pt-0.5">{v.comment}</span>
                              )}
                            </div>
                            {v.comment && user && voteUsername && (
                              isReplyingToThis ? (
                                <div className="flex gap-2 ml-7" onClick={(e) => e.stopPropagation()}>
                                  <textarea
                                    className="flex-1 border rounded p-1.5 text-xs resize-none"
                                    rows={2}
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    maxLength={500}
                                    autoFocus
                                  />
                                  <div className="flex flex-col gap-1 justify-end shrink-0">
                                    <button
                                      onClick={handleReplySubmit}
                                      disabled={isReplySubmitting || !replyText.trim()}
                                      className="px-2 py-1 bg-green-600 text-white text-xs rounded disabled:opacity-50"
                                    >
                                      送信
                                    </button>
                                    <button
                                      onClick={() => { setReplyingToVoteId(null); setReplyText(''); }}
                                      className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600"
                                    >
                                      キャンセル
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  className="ml-7 text-xs text-gray-400 hover:text-blue-500"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setReplyingToVoteId(v.id);
                                    setReplyText(`@${voteUsername} `);
                                  }}
                                >
                                  返信
                                </button>
                              )
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {hiddenCount > 0 && (
              <button
                onClick={() => setShowAllResults((v) => !v)}
                className="w-full text-sm text-gray-500 hover:text-gray-700 py-1 flex items-center justify-center gap-1"
              >
                {showAllResults ? <>折りたたむ ▲</> : <>さらに{hiddenCount}件を表示 ▼</>}
              </button>
            )}
          </div>
        )}

        {/* Voting form */}
        {user ? (
          <div className="space-y-3">
            {myVote ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  投票済み: <strong>{getMyVoteLabel(myVote)}</strong>
                </p>
                <a
                  href={`https://x.com/intent/post?text=${encodeURIComponent(
                    situationType === 'naki'
                      ? `【鳴く？鳴かない？】${title ?? '麻雀の局面'}\n${getMyVoteLabel(myVote)}！ #なにきる #麻雀\n`
                      : `【みんなで何切る】${title ?? '麻雀の局面'}\n${getMyVoteLabel(myVote)}！ #みんなで何切る #麻雀\n`
                  )}&url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
                  </svg>
                  Xでシェア
                </a>
              </div>
            ) : situationType === 'naki' ? (
              /* 鳴き判断 */
              <>
                <div>
                  <div className="grid grid-cols-2 gap-2">
                    {(['chi', 'pon', 'kan', 'none'] as NakiAction[]).map((action) => (
                      <label
                        key={action}
                        className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-colors
                          ${nakiAction === action ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <input
                          type="radio"
                          name="naki_action"
                          value={action}
                          checked={nakiAction === action}
                          onChange={() => setNakiAction(action)}
                          className="accent-green-600"
                        />
                        <span className="text-sm font-medium text-gray-700">{NAKI_LABELS[action]}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <textarea
                  className="w-full border rounded p-2 text-sm resize-none"
                  rows={2}
                  placeholder="コメント（任意・500文字以内）"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={500}
                />
                <div className="flex justify-end">
                  <Button onClick={handleSubmit} loading={isSubmitting} disabled={!canSubmit}>
                    投票する
                  </Button>
                </div>
              </>
            ) : (
              /* 門前 or 副露後 */
              <>
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-2">
                    切る牌を選択
                    {selectedTile && (
                      <span className="ml-2 text-green-600">
                        → {getTileLabel(selectedTile)}{riichi ? ' リーチ' : ''} を選択中
                      </span>
                    )}
                  </div>
                  <TileSelector
                    onSelect={(t) => setSelectedTile(t)}
                    selectedTile={selectedTile ?? undefined}
                  />
                </div>
                {situationType === 'menzen' && (
                  <label className="flex items-center gap-2 cursor-pointer w-fit">
                    <input
                      type="checkbox"
                      checked={riichi}
                      onChange={(e) => setRiichi(e.target.checked)}
                      className="accent-green-600 w-4 h-4"
                    />
                    <span className="text-sm font-medium text-gray-700">リーチ</span>
                  </label>
                )}
                <textarea
                  className="w-full border rounded p-2 text-sm resize-none"
                  rows={2}
                  placeholder="コメント（任意・500文字以内）"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={500}
                />
                <div className="flex justify-end">
                  <Button onClick={handleSubmit} loading={isSubmitting} disabled={!canSubmit}>
                    投票する
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-2">
            投票するには
            <button onClick={login} className="text-blue-500 hover:underline mx-1">Xでログイン</button>
            が必要です
          </p>
        )}

        {/* Comment thread */}
        <div className="border-t -mx-4 -mb-4">
          <CommentThread handId={handId} inline />
        </div>
      </div>
    </div>
  );
}
